// src/components/profile/ProfessionalProfile.jsx
import React from 'react';
import EditableSection from './EditableSection';
import EmptyStatePrompt from './EmptyStatePrompt';

const ProfessionalProfile = ({
  userData,
  isOwnProfile,
  editSections,
  formData,
  handleFormChange,
  handleArrayChange,
  addArrayItem,
  removeArrayItem,
  toggleEditMode,
  saveChanges,
  cancelEdit
}) => {
  return (
    <div className="mt-4 mb-8 bg-dark-gray/30 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all duration-300">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        Professional Profile
      </h2>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          {/* About Section */}
          <EditableSection
            title="About"
            icon={
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
            isEditing={editSections.about}
            onEdit={() => toggleEditMode('about')}
            onSave={() => saveChanges('about')}
            onCancel={() => cancelEdit('about')}
            isOwnProfile={isOwnProfile}
          >
            {editSections.about ? (
              <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-4">
                <label className="block text-gray-300 mb-1">Professional Summary</label>
                <textarea
                  value={formData.about.summary}
                  onChange={(e) => handleFormChange('about', 'summary', e.target.value)}
                  rows={4}
                  maxLength={500} // Enforce character limit
                  className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Tell us about your professional background and expertise..."
                />
                <div className={`text-right text-xs mt-1 ${formData.about.summary.length > 500 ? 'text-red-500' : (formData.about.summary.length > 450 ? 'text-orange-400' : 'text-gray-400')}`}>
                  {formData.about.summary.length}/500
                </div>
              </div>
            ) : (
              userData.professional.summary ? (
                <p className="text-gray-300 leading-relaxed">{userData.professional.summary}</p>
              ) : (
                isOwnProfile && <EmptyStatePrompt section="About" onAdd={() => toggleEditMode('about')} />
              )
            )}
          </EditableSection>
          
          {/* Services Section */}
          <EditableSection
            title="Services Offered"
            icon={
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
            isEditing={editSections.services}
            onEdit={() => toggleEditMode('services')}
            onSave={() => saveChanges('services')}
            onCancel={() => cancelEdit('services')}
            isOwnProfile={isOwnProfile}
          >
            {editSections.services ? (
              <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-4">
                <div className="space-y-3">
                  {formData.services.map((service, index) => (
                    <div key={index} className="flex items-center">
                      <input
                        type="text"
                        value={service}
                        onChange={(e) => handleArrayChange('services', index, null, e.target.value)}
                        className="flex-grow bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., Signature Analysis"
                      />
                      <button 
                        type="button"
                        onClick={() => removeArrayItem('services', index)}
                        className="ml-2 text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={() => addArrayItem('services', '')}
                  className="mt-4 w-full py-2 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                >
                  + Add Service
                </button>
              </div>
            ) : (
              userData.professional.services && userData.professional.services.length > 0 ? (
                <ul className="space-y-2">
                  {userData.professional.services.map((service, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-300">{service}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                isOwnProfile && <EmptyStatePrompt section="Services" onAdd={() => toggleEditMode('services')} />
              )
            )}
          </EditableSection>
          
          {/* Certifications Section */}
          <EditableSection
            title="Certifications"
            icon={
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
            isEditing={editSections.certifications}
            onEdit={() => toggleEditMode('certifications')}
            onSave={() => saveChanges('certifications')}
            onCancel={() => cancelEdit('certifications')}
            isOwnProfile={isOwnProfile}
          >
            {editSections.certifications ? (
              <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-4">
                <div className="space-y-4">
                  {formData.certifications.map((cert, index) => (
                    <div key={index} className="p-4 border border-gray-700 rounded-lg bg-dark-gray/30 relative">
                      <button 
                        type="button"
                        onClick={() => removeArrayItem('certifications', index)}
                        className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-gray-300 mb-1">Name</label>
                          <input
                            type="text"
                            value={cert.name || ''}
                            onChange={(e) => handleArrayChange('certifications', index, 'name', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., Certified Graphologist"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 mb-1">Organization</label>
                          <input
                            type="text"
                            value={cert.org || ''}
                            onChange={(e) => handleArrayChange('certifications', index, 'org', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., Graphology Institute of India"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-300 mb-1">Year</label>
                        <input
                          type="text"
                          value={cert.year || ''}
                          onChange={(e) => handleArrayChange('certifications', index, 'year', e.target.value)}
                          className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="e.g., 2015"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={() => addArrayItem('certifications', { name: '', org: '', year: '' })}
                  className="mt-4 w-full py-2 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                >
                  + Add Certification
                </button>
              </div>
            ) : (
              userData.professional.certifications && userData.professional.certifications.length > 0 ? (
                <ul className="space-y-3">
                  {userData.professional.certifications.map((cert, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div>
                        <div className="font-medium">{cert.name}</div>
                        <div className="text-sm text-gray-400">{cert.org}, {cert.year}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                isOwnProfile && <EmptyStatePrompt section="Certifications" onAdd={() => toggleEditMode('certifications')} />
              )
            )}
          </EditableSection>
        </div>
        
        <div>
          {/* Contact Section */}
          <EditableSection
            title="Contact Information"
            icon={
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
            isEditing={editSections.contact}
            onEdit={() => toggleEditMode('contact')}
            onSave={() => saveChanges('contact')}
            onCancel={() => cancelEdit('contact')}
            isOwnProfile={isOwnProfile}
          >
            {editSections.contact ? (
              <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.contact.email || ''}
                      onChange={(e) => handleFormChange('contact', 'email', e.target.value)}
                      className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="e.g., you@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-1">Website</label>
                    <input
                      type="text"
                      value={formData.contact.website || ''}
                      onChange={(e) => handleFormChange('contact', 'website', e.target.value)}
                      className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="e.g., https://yourwebsite.com"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.contact.available || false}
                      onChange={(e) => handleFormChange('contact', 'available', e.target.checked)}
                      className="accent-primary"
                    />
                    <label className="text-gray-300">Available for new clients</label>
                  </div>
                </div>
              </div>
            ) : (
              userData.professional.email || userData.professional.website ? (
                <ul className="space-y-3">
                  {userData.professional.email && (
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${userData.professional.email}`} className="text-blue-400 hover:underline">{userData.professional.email}</a>
                    </li>
                  )}
                  {userData.professional.website && (
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <a href={userData.professional.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{userData.professional.website}</a>
                    </li>
                  )}
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={userData.professional.available ? "text-green-400" : "text-red-400"}>
                      {userData.professional.available ? "Currently Accepting Clients" : "Not Available for New Clients"}
                    </span>
                  </li>
                </ul>
              ) : (
                isOwnProfile && <EmptyStatePrompt section="Contact Information" onAdd={() => toggleEditMode('contact')} />
              )
            )}
          </EditableSection>
          
          {/* Education Section */}
          <EditableSection
            title="Education"
            icon={
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
            }
            isEditing={editSections.education}
            onEdit={() => toggleEditMode('education')}
            onSave={() => saveChanges('education')}
            onCancel={() => cancelEdit('education')}
            isOwnProfile={isOwnProfile}
          >
            {editSections.education ? (
              <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-4">
                <div className="space-y-4">
                  {formData.education.map((edu, index) => (
                    <div key={index} className="p-4 border border-gray-700 rounded-lg bg-dark-gray/30 relative">
                      <button 
                        type="button"
                        onClick={() => removeArrayItem('education', index)}
                        className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-gray-300 mb-1">Degree</label>
                          <input
                            type="text"
                            value={edu.degree || ''}
                            onChange={(e) => handleArrayChange('education', index, 'degree', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., PhD in Psychology"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 mb-1">Institution</label>
                          <input
                            type="text"
                            value={edu.institution || ''}
                            onChange={(e) => handleArrayChange('education', index, 'institution', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., IIT Bombay"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-300 mb-1">Year</label>
                          <input
                            type="text"
                            value={edu.year || ''}
                            onChange={(e) => handleArrayChange('education', index, 'year', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., 2008-2013"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 mb-1">Description</label>
                          <input
                            type="text"
                            value={edu.description || ''}
                            onChange={(e) => handleArrayChange('education', index, 'description', e.target.value)}
                            className="w-full bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="e.g., Graduated with distinction"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={() => addArrayItem('education', { degree: '', institution: '', year: '', description: '' })}
                  className="mt-4 w-full py-2 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                >
                  + Add Education
                </button>
              </div>
            ) : (
              userData.professional.education && userData.professional.education.length > 0 ? (
                <div className="space-y-4">
                  {userData.professional.education.map((edu, index) => (
                    <div key={index} className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between mb-1">
                        <h4 className="font-medium text-white">{edu.degree}</h4>
                        <span className="text-sm text-gray-400">{edu.year}</span>
                      </div>
                      <p className="text-sm text-gray-300">{edu.institution}</p>
                      {edu.description && (
                        <p className="text-sm text-gray-400 mt-1">{edu.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                isOwnProfile && <EmptyStatePrompt section="Education" onAdd={() => toggleEditMode('education')} />
              )
            )}
          </EditableSection>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfile;
